import { useEffect, useMemo, useState } from "react";
import { useTables } from "@/hooks/use-tables";
import { useActiveReservations } from "@/hooks/use-reservations";
import { useDaySummary } from "@/hooks/use-day-summary";
import { useAvailableDates } from "@/hooks/use-available-dates";
import { useActiveLocationTimezone } from "@/hooks/use-locations";
import {
  cancelReservation,
  markCompleted,
  markNoShow,
  markSeated,
  useAppStore,
  useTodayReservations,
} from "@/store/store";
import type { Reservation } from "@/store/types";
import { dayjs, getWeekDays, todayStr } from "@/utils/date";
import { useNavigate, useSearch } from "@tanstack/react-router";

// Re-declared locally to break the import cycle with the route file.
// Kept in sync with ``ReservasSearch`` in ``src/routes/reservas.tsx``.
type ReservasView = "table" | "timeline" | "dashboard";
import { BarChart3, CalendarRange, ChevronLeft, List } from "lucide-react";
import TimelineGrid from "./TimelineGrid";
import DayReportView from "./DayReportView";
import Sidebar from "./Sidebar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { reservationTagLabel } from "@/lib/reservation-tags";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function formatSelectedDate(dateStr: string): string {
  const d = dayjs(dateStr);
  return `${d.format("dddd, D [de] MMMM YYYY").replace(/^\w/, (c) => c.toUpperCase())}`;
}

const statusBadge: Record<string, { label: string; cls: string }> = {
  cancelada: { cls: "bg-gray-100 text-gray-500 border-transparent", label: "Cancelado" },
  completada: { cls: "bg-gray-100 text-gray-600 border-transparent", label: "Completado" },
  noshow: { cls: "bg-red-100 text-red-700 border-transparent", label: "No Show" },
  reservada: { cls: "bg-yellow-100 text-yellow-700 border-transparent", label: "Activa" },
  sentado: { cls: "bg-status-seated text-white border-transparent", label: "Sentado" },
};

function TableView() {
  const todayReservations = useTodayReservations();
  const tables = useAppStore((s) => s.tables);
  const { isPending: isTablesPending } = useTables();
  const { isPending: isReservationsPending } = useActiveReservations();
  const isInitialLoad = isTablesPending || isReservationsPending;

  const activeReservations = todayReservations.filter((r) => r.status !== "cancelada");
  const groups: Record<string, Reservation[]> = {};
  for (const r of activeReservations) {
    (groups[r.time] ??= []).push(r);
  }

  if (isInitialLoad) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Spinner className="size-8 text-text-secondary" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6">
      {Object.keys(groups).length === 0 && (
        <div className="text-center text-text-muted py-12">No hay reservas para este día</div>
      )}
      {Object.entries(groups).map(([time, resos]) => (
        <div className="mb-8" key={time}>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm font-bold">{time}</span>
            <span className="text-xs text-text-muted">{resos.length} reservas</span>
          </div>
          {/* Card list on phone (<sm) — table on sm+ to avoid horizontal scroll */}
          <div className="flex flex-col gap-2 sm:hidden">
            {resos.map((r) => {
              const badge = statusBadge[r.status] || statusBadge.reservada;
              const tableName = r.tableIds
                .map((id) => tables.find((t) => t.id === id)?.name || "Mesa")
                .join(" + ");
              return (
                <div
                  key={r.id}
                  className="rounded-lg border border-border/60 bg-white p-3 flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.guestName}</div>
                      <div className="text-xs text-text-secondary truncate">
                        {r.pax} personas · {tableName || "Sin mesa"}
                      </div>
                    </div>
                    <Badge className={`text-[11px] font-semibold shrink-0 ${badge.cls}`}>
                      {badge.label}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.status === "reservada" && (
                      <>
                        <Button
                          onClick={() => markSeated(r.id)}
                          size="sm"
                          className="bg-green-600 text-white hover:bg-green-700"
                        >
                          Sentar
                        </Button>
                        <Button
                          onClick={() => markNoShow(r.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          No-show
                        </Button>
                        <Button
                          onClick={() => cancelReservation(r.id, "Cancelado")}
                          size="sm"
                          variant="outline"
                          className="text-text-secondary"
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                    {r.status === "sentado" && (
                      <Button
                        onClick={() => markCompleted(r.id)}
                        size="sm"
                        className="bg-dark text-white"
                      >
                        Completar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <Table className="hidden sm:table">
            <TableHeader>
              <TableRow className="text-xs text-text-secondary border-b border-border">
                <TableHead className="pb-2 font-medium w-[20%]">Nombre</TableHead>
                <TableHead className="pb-2 font-medium w-[8%]">Pax</TableHead>
                <TableHead className="pb-2 font-medium w-[14%]">Mesa</TableHead>
                <TableHead className="pb-2 font-medium w-[12%]">Estado</TableHead>
                <TableHead className="pb-2 font-medium w-[10%]">Llegada</TableHead>
                <TableHead className="pb-2 font-medium w-[10%]">Tags</TableHead>
                <TableHead className="pb-2 font-medium w-[20%]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resos.map((r) => {
                const badge = statusBadge[r.status] || statusBadge.reservada;
                const tableName = r.tableIds
                  .map((id) => tables.find((t) => t.id === id)?.name || "Mesa")
                  .join(" + ");
                return (
                  <TableRow
                    key={r.id}
                    className="border-b border-border/50 hover:bg-surface-alt/50"
                  >
                    <TableCell className="py-3 font-medium">{r.guestName}</TableCell>
                    <TableCell className="py-3">{r.pax}</TableCell>
                    <TableCell className="py-3 text-text-secondary">
                      {tableName || "Sin mesa"}
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge className={`text-[11px] font-semibold ${badge.cls}`}>
                        {badge.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 text-text-secondary">{r.seatedAt || "—"}</TableCell>
                    <TableCell className="py-3">
                      <div className="flex gap-1">
                        {r.tags.map((t) => (
                          <span
                            key={t}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-surface-alt text-text-secondary"
                          >
                            {reservationTagLabel(t)}
                          </span>
                        ))}
                        {r.isVip && <span className="text-orange-500 text-xs">★</span>}
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="flex gap-1.5">
                        {r.status === "reservada" && (
                          <>
                            <Button
                              onClick={() => markSeated(r.id)}
                              size="xs"
                              className="bg-green-600 text-white hover:bg-green-700"
                            >
                              Sentar
                            </Button>
                            <Button
                              onClick={() => markNoShow(r.id)}
                              size="xs"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              No-show
                            </Button>
                            <Button
                              onClick={() => cancelReservation(r.id, "Cancelado")}
                              size="xs"
                              variant="outline"
                              className="border-border text-text-secondary hover:bg-gray-50"
                            >
                              Cancelar
                            </Button>
                          </>
                        )}
                        {r.status === "sentado" && (
                          <Button
                            onClick={() => markCompleted(r.id)}
                            size="xs"
                            className="bg-dark text-white"
                          >
                            Completar
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ))}

      {/* Summary */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-4 border-t border-border text-xs text-text-secondary">
        <span>
          Total: {activeReservations.length} reservas ·{" "}
          {activeReservations.reduce((s, r) => s + r.pax, 0)} personas
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-status-seated" />{" "}
          {activeReservations.filter((r) => r.status === "sentado").length} sentados
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-reserved-border" />{" "}
          {activeReservations.filter((r) => r.status === "reservada").length} activas
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" />{" "}
          {activeReservations.filter((r) => r.status === "noshow").length} no show
        </span>
      </div>
    </div>
  );
}

/** Wipe every piece of Sidebar-related state ``ReservasPage`` controls
 *  from the global store: the detail panel, the in-flight Nueva
 *  reserva draft, and the sheet visibility flag. Used in three
 *  places (mount, unmount, backdrop / Escape close) — keeping one
 *  function avoids drift between them. Reads via ``getState()``
 *  because the callers either fire from an effect cleanup or an
 *  event handler where the React-bound setters may already be stale. */
function clearReservasPageSidebarState() {
  const s = useAppStore.getState();
  s.closeNewReservation();
  s.setDetailReservationId(null);
  s.setShowSidebar(false);
}

export default function ReservasPage() {
  const openNewReservation = useAppStore((s) => s.openNewReservation);
  const setSelectedDate = useAppStore((s) => s.setSelectedDate);
  const showSidebar = useAppStore((s) => s.showSidebar);
  const setShowSidebar = useAppStore((s) => s.setShowSidebar);
  const locationTz = useActiveLocationTimezone();
  const navigate = useNavigate({ from: "/reservas" });
  const search = useSearch({ from: "/reservas" });

  // The Sidebar state (detail panel, "Nueva reserva" surface, sheet
  // visibility) lives in the global store so both this page and the
  // dashboard at ``/`` can drive it. But the host expects each page
  // to be its own session: opening a reservation detail (or starting
  // a new one) inside the Reservas timeline shouldn't carry over to
  // the floor map, and vice-versa. Clear the state on enter and on
  // leave so each navigation lands on a fresh Sidebar.
  useEffect(() => {
    clearReservasPageSidebarState();
    return clearReservasPageSidebarState;
  }, []);

  // The sheet on this page is a single-purpose surface: it exists to
  // show a reservation's detail or the "Nueva reserva" flow. Once
  // both close (operator clicked Volver), the sheet has nothing
  // meaningful to render, so dismiss it instead of falling back to
  // the dashboard's default Sidebar landing view (the reservation
  // list). That landing view belongs to the floor map experience,
  // not to the timeline.
  //
  // ⚠️ This relies on React batching the two state updates the
  // entry points fire back-to-back in the same event handler
  // (``setShowSidebar(true) + openNewReservation()`` from the
  // header CTA / FAB, ``setDetailReservationId(id) +
  // setShowSidebar(true)`` from a reservation-bar tap). Both writes
  // commit before the effect re-runs, so the sheet never flashes
  // closed in the gap. Never insert ``await`` / ``setTimeout``
  // between these two calls in any entry point — the effect would
  // close the sheet between the writes.
  const detailReservationId = useAppStore((s) => s.detailReservationId);
  const newReservationOpen = useAppStore((s) => s.newReservationOpen);
  useEffect(() => {
    if (showSidebar && detailReservationId === null && !newReservationOpen) {
      setShowSidebar(false);
    }
  }, [showSidebar, detailReservationId, newReservationOpen, setShowSidebar]);

  const { data: availableDates } = useAvailableDates();

  // Effective values: explicit URL > next reservable date > today.
  // We hold off rendering the heavy children until we have *something*
  // to anchor to so that ``useActiveReservations`` doesn't fire with
  // a date the user is about to be redirected away from.
  const todayLocal = todayStr(locationTz);
  const effectiveDate = search.date ?? availableDates?.find((d) => d >= todayLocal) ?? todayLocal;
  const effectiveView: ReservasView = search.view ?? "timeline";

  // Canonicalise the URL once defaults resolve so the page is shareable
  // even when the operator opened ``/reservas`` cold.
  useEffect(() => {
    if (!availableDates) return;
    if (search.date && search.view) return;
    navigate({
      search: (s: Record<string, unknown>) => ({
        ...s,
        view: (s.view as ReservasView | undefined) ?? effectiveView,
        date: (s.date as string | undefined) ?? effectiveDate,
      }),
      replace: true,
    });
  }, [availableDates, search.date, search.view, effectiveView, effectiveDate, navigate]);

  // Mirror the URL date into the store so the existing hooks
  // (``useActiveReservations``, ``useTodayReservations``) keep working
  // unchanged. The URL is the source of truth on this page; the store
  // copy is just a relay for the data layer.
  useEffect(() => {
    if (effectiveDate !== useAppStore.getState().selectedDate) {
      setSelectedDate(effectiveDate);
    }
  }, [effectiveDate, setSelectedDate]);

  // Week offset stays local — it's only the visual cursor for the day
  // picker, not addressable state. Reset to the week containing the
  // effective date when it changes.
  const [weekOffset, setWeekOffset] = useState(0);
  const weekDays = useMemo(() => getWeekDays(weekOffset), [weekOffset]);
  const summary = useDaySummary(weekDays[0]?.date, weekDays[weekDays.length - 1]?.date);
  const summaryByDate = useMemo(
    () => new Map((summary.data ?? []).map((row) => [row.date, row])),
    [summary.data],
  );

  const setDate = (date: string) =>
    navigate({
      search: (s: Record<string, unknown>) => ({ ...s, date }),
      replace: false,
    });

  const setView = (view: ReservasView) =>
    navigate({
      search: (s: Record<string, unknown>) => ({ ...s, view }),
      replace: false,
    });

  // Mobile-only: clearing ``view`` makes the route fall back to the Sidebar
  // (the host's phone list). On desktop this button is hidden — ReservasPage
  // is the page itself, reached via the nav rail.
  const exitToList = () =>
    navigate({
      search: (s: Record<string, unknown>) => ({ ...s, view: undefined }),
      replace: false,
    });

  return (
    <div className="flex flex-col flex-1 min-h-0 md:pl-16">
      {/* Page header */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 sm:px-8 py-4 sm:py-5">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={exitToList}
            aria-label="Volver a la lista"
            className="md:hidden h-9 w-9 p-0 rounded-[10px] border border-input bg-white text-text-primary"
          >
            <ChevronLeft className="h-[18px] w-[18px]" />
          </Button>
          <h1 className="text-xl font-bold">Reservas</h1>
          <span className="text-sm text-text-secondary">{formatSelectedDate(effectiveDate)}</span>
        </div>
        <Button
          onClick={() => {
            // Open the Sidebar as a Sheet on top of the Reservas page
            // first so the in-sidebar Nueva reserva surface has a host
            // to render into. ``openNewReservation`` flips the inner
            // state but doesn't touch the sheet itself by design (it
            // shouldn't double-stack with the always-visible desktop
            // sidebar at ``/``; here we explicitly want the sheet).
            // Also drop any lingering ``detailReservationId`` so the
            // sheet doesn't render a stale detail panel from a prior
            // tap that the operator dismissed by clicking outside.
            useAppStore.getState().setDetailReservationId(null);
            setShowSidebar(true);
            openNewReservation();
          }}
          // Hidden on mobile because the bottom-nav FAB already covers
          // this affordance there (same Sidebar surface lands the same
          // way). Kept on md+ where there is no FAB.
          className="hidden md:inline-flex bg-dark text-white hover:bg-dark/90 font-semibold gap-2"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Nueva Reserva
        </Button>
      </div>

      {/* Day selector */}
      <div className="px-4 sm:px-8 pb-4">
        {/* Month + nav row */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold">
            {dayjs(weekDays[0].date)
              .format("MMMM")
              .replace(/^\w/, (c) => c.toUpperCase())}
          </span>
          <div className="flex items-center gap-1">
            <Button
              onClick={() => setWeekOffset((o) => o - 1)}
              size="icon-sm"
              variant="ghost"
              className="text-text-secondary"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </Button>
            <Button
              onClick={() => setWeekOffset((o) => o + 1)}
              size="icon-sm"
              variant="ghost"
              className="text-text-secondary"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Button>
            <Button
              onClick={() => {
                setWeekOffset(0);
                setDate(todayLocal);
              }}
              size="xs"
              variant="outline"
              className="ml-1 text-text-secondary border-border"
            >
              Hoy
            </Button>
          </div>
        </div>
        {/* Week days row */}
        <div className="flex gap-1">
          {weekDays.map((day) => {
            const row = summaryByDate.get(day.date);
            const isSelected = effectiveDate === day.date;
            return (
              <Button
                key={day.date}
                onClick={() => setDate(day.date)}
                variant="ghost"
                className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 h-auto rounded-lg
                ${
                  isSelected
                    ? "bg-dark text-white hover:bg-dark/90"
                    : "text-text-secondary hover:bg-surface-alt"
                }`}
              >
                <span className="text-[10px] font-medium opacity-70">{day.short}</span>
                <span className="text-sm font-semibold">{day.num}</span>
                {/* Reserve a fixed-height row so the buttons don't grow
                    once the summary lands — avoids layout shift. */}
                <span className="h-3 text-[9px] leading-3 opacity-70">
                  {row && row.count > 0 ? `${row.count}r · ${row.pax}p` : ""}
                </span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* View toggle */}
      <div className="px-4 sm:px-8 pb-4 flex-1 min-h-0 flex flex-col">
        <Tabs
          value={effectiveView}
          onValueChange={(v) => setView(v as ReservasView)}
          className="flex-1 min-h-0"
        >
          {/* The view picker is desktop-only — on mobile, Timeline and
              Reportes live behind the kebab in the bottom nav, and the
              Reservas tab itself drops you straight into the default
              (table) view. Hiding the pill here keeps the same nav
              available without duplicating affordances. */}
          <TabsList variant="default" className="hidden md:flex gap-0.5">
            <TabsTrigger value="table" title="Vista tabla">
              <List size={16} strokeWidth={2} />
            </TabsTrigger>
            <TabsTrigger value="timeline" title="Vista timeline">
              <CalendarRange size={16} strokeWidth={2} />
            </TabsTrigger>
            <TabsTrigger value="dashboard" title="Reporte del día">
              <BarChart3 size={16} strokeWidth={2} />
            </TabsTrigger>
          </TabsList>

          <div className="hidden md:block border-t border-border mt-4" />

          <TabsContent value="table" className="flex flex-col min-h-0">
            <TableView />
          </TabsContent>
          <TabsContent value="timeline" className="flex flex-col min-h-0">
            <TimelineGrid />
          </TabsContent>
          <TabsContent value="dashboard" className="flex flex-col min-h-0">
            <DayReportView />
          </TabsContent>
        </Tabs>
      </div>

      {/* Sidebar overlay — the Reservas page is a full-page surface
          and doesn't mount the Sidebar inline (the desktop dashboard
          at ``/`` does). To keep the "+ Nueva reserva" button and the
          per-reservation tap-to-open detail flow working, render the
          Sidebar inside a sheet driven by the same ``showSidebar``
          flag the dashboard uses. ``openNewReservation`` and
          ``setDetailReservationId`` already feed the Sidebar's
          internal state; the sheet is just where it lands. */}
      <Sheet
        open={showSidebar}
        // Closing the sheet from outside (tap on the backdrop,
        // Escape) clears the in-flight detail / Nueva reserva so a
        // later tap doesn't surface yesterday's selection. The
        // auto-close effect above runs after Volver from inside
        // the sidebar and keeps the same invariant on that path.
        onOpenChange={(open) => {
          if (!open) {
            // ``clearReservasPageSidebarState`` also sets
            // ``setShowSidebar(false)``; that's fine — the Sheet's
            // own ``setShowSidebar(open)`` call below would do the
            // same and Zustand de-dupes the no-op write.
            clearReservasPageSidebarState();
          }
          setShowSidebar(open);
        }}
      >
        <SheetContent
          side="right"
          className="w-full max-w-105 p-0 pointer-coarse:max-w-[min(100vw,28rem)]"
          showCloseButton={false}
        >
          <Sidebar />
        </SheetContent>
      </Sheet>
    </div>
  );
}
